/**
 * /road-to-michigan — "THE DILA ROAD TO MICHIGAN // THE CHILA RUN"
 *
 * Illustrated tie-dye US map command center. Coin-badge markers, custom Kia
 * Telluride sprite at the live check-in, boys' avatar heads, cinematic
 * slide-over panel, Go Live broadcast terminal (Kick/Twitch), tip footer.
 * Route/stops/current check-in are admin-managed via /run/tracker (/admin/run).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, X, Play, ExternalLink, Radio, Loader2, ArrowRight } from 'lucide-react';
import api from '../services/api';

const GOLD = '#C9A961';
const MAP_IMG = 'https://static.prod-images.emergentagent.com/jobs/9a211650-b78b-41f7-9969-b52217e0c686/images/26f0f0d8272654162ea270b7ed5c08e19af921f95b6bc84a0e04d0b2dedc54ab.png';

// Illustrated-map projection (continental US -> % of the tie-dye image).
const LON_MIN = -125, LON_MAX = -66, LAT_MIN = 24, LAT_MAX = 50;
const X0 = 7, X1 = 93, Y0 = 16, Y1 = 82;
const project = (lat, lon) => ({
  left: X0 + ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * (X1 - X0),
  top: Y0 + ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * (Y1 - Y0),
});

const glyphFor = (kind) => (kind === 'start' ? '🚀' : kind === 'destination' ? '⭐' : kind === 'heritage' ? '🏙️' : '🏀');

const RoadToMichigan = () => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('bucket');
  const [selected, setSelected] = useState(null);
  const [activeBoy, setActiveBoy] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const MEDIA = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/run/media/${p}`;
  const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

  useEffect(() => {
    api.get('/run/tracker').then((r) => setCfg(r.data.tracker)).catch(() => setCfg({ stops: [], boys: {} }));
  }, []);

  const openStop = (stop, boyId = null) => { setSelected(stop); setActiveBoy(boyId || (stop.boys || [])[0]); };

  const stops = cfg?.stops || [];
  const boysMap = cfg?.boys || {};
  const current = stops.find((s) => s.id === cfg?.current_stop_id) || stops[0];
  const pts = stops.map((s) => project(s.lat, s.lon));
  const routeD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.left} ${p.top}`).join(' ');

  const Head = ({ boyKey, i }) => {
    const b = boysMap[boyKey] || { initials: boyKey, name: boyKey };
    return (
      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black text-black border-2 border-[#0b0b0d] overflow-hidden shadow" style={{ marginLeft: i > 0 ? -5 : 0, background: GOLD }}>
        {b.avatar ? <img src={b.avatar} alt={b.name} className="w-full h-full object-cover" /> : b.initials}
      </span>
    );
  };

  const Coin = ({ stop }) => {
    const p = project(stop.lat, stop.lon);
    const isDest = stop.kind === 'destination';
    const dim = tab === 'scout' && !isDest;
    return (
      <button
        onClick={() => openStop(stop)}
        data-testid={`r2m-coin-${stop.id}`}
        className="absolute flex flex-col items-center transition-all duration-500 hover:z-30 hover:scale-110 focus:outline-none"
        style={{ left: `${p.left}%`, top: `${p.top}%`, transform: 'translate(-50%,-50%)', opacity: dim ? 0.25 : 1, zIndex: isDest ? 20 : 10 }}
      >
        {(stop.boys || []).length > 0 && (
          <span className="flex items-center mb-0.5">{stop.boys.map((b, i) => <Head key={b} boyKey={b} i={i} />)}</span>
        )}
        <span
          className="flex items-center justify-center rounded-full text-base sm:text-lg leading-none"
          style={{
            width: 40, height: 40,
            background: isDest ? '#0b0b0d' : '#0b0b0d',
            border: `3px solid ${GOLD}`,
            boxShadow: `0 3px 12px rgba(0,0,0,0.6)${isDest ? `, 0 0 16px ${GOLD}` : ''}`,
          }}
        >
          {glyphFor(stop.kind)}
        </span>
      </button>
    );
  };

  const TABS = [
    { id: 'bucket', label: 'Bucket Tracker', emoji: '🏀' },
    { id: 'scout', label: 'Scout View', emoji: '🎓' },
    { id: 'live', label: 'Go Live', emoji: '🎥' },
  ];

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-[#f4efe6]" data-testid="road-to-michigan">
      {/* Header */}
      <div className="px-5 lg:px-10 pt-8 pb-5 max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase font-black text-white/50 hover:text-white mb-6">
          <ArrowLeft className="w-3 h-3" /> PRSVR
        </Link>
        <h1 className="text-4xl sm:text-6xl font-black uppercase leading-[0.88] tracking-tight">{cfg?.title || 'The DILA Road To Michigan'}</h1>
        <p className="mt-3 text-base sm:text-2xl tracking-[0.15em] uppercase text-white/45 font-black">{cfg?.subtitle || 'Destination: Get Buckets'}</p>
      </div>

      {/* SECTION 1: System Controls */}
      <div className="sticky top-0 z-30 bg-[#0b0b0d]/95 backdrop-blur border-y border-white/10" data-testid="r2m-controls">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-3 flex gap-2 sm:gap-3">
          {TABS.map((t) => {
            const active = tab === t.id;
            const isLive = t.id === 'live';
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`r2m-tab-${t.id}`}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-3.5 text-[11px] sm:text-sm font-black tracking-[0.18em] uppercase transition-colors border-2 ${active ? 'bg-[#C9A961] text-black border-[#C9A961]' : 'bg-[#161616] text-white/75 border-white/15 hover:border-[#C9A961]'}`}>
                <span>{t.emoji}</span>{t.label}
                {isLive && <span className="relative inline-flex w-2.5 h-2.5 ml-1"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" /></span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* SECTION 2/3: Map or Live terminal */}
      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-6">
        {!cfg ? (
          <div className="aspect-[3/2] flex items-center justify-center border border-white/10"><Loader2 className="w-8 h-8 animate-spin text-[#C9A961]" /></div>
        ) : tab === 'live' ? (
          <div className="border-2 border-white/10 bg-black" data-testid="r2m-live-terminal">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
              <Radio className="w-4 h-4 text-red-500 animate-pulse" />
              <span className="text-[10px] tracking-[0.3em] uppercase font-black text-white/70">Live Broadcast · PRISM</span>
            </div>
            <div className="grid lg:grid-cols-2 gap-3 p-3">
              <div className="border border-white/10">
                <div className="px-3 py-2 text-[10px] tracking-[0.25em] uppercase font-black text-[#53fc18] border-b border-white/10">Kick · @prsvr</div>
                <iframe title="Kick" src="https://player.kick.com/prsvr?autoplay=false&muted=true" height="380" width="100%" frameBorder="0" scrolling="no" allowFullScreen />
              </div>
              <div className="border border-white/10">
                <div className="px-3 py-2 text-[10px] tracking-[0.25em] uppercase font-black text-[#a970ff] border-b border-white/10">Twitch · prsvr</div>
                <iframe title="Twitch" src={`https://player.twitch.tv/?channel=prsvr&parent=${host}&autoplay=false`} height="380" width="100%" frameBorder="0" scrolling="no" allowFullScreen />
              </div>
            </div>
          </div>
        ) : (
          <div className="relative w-full aspect-[3/2] overflow-hidden border-2 border-white/10 select-none" data-testid="r2m-map">
            <img src={MAP_IMG} alt="Tie-dye route map of the United States" className="absolute inset-0 w-full h-full object-contain" draggable="false" />
            {/* Route line */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d={routeD} fill="none" stroke={GOLD} strokeWidth="0.6" strokeDasharray="0.4,1.6" strokeLinecap="round" opacity={tab === 'scout' ? 0.2 : 0.95} vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2 }} />
            </svg>
            {/* Coins */}
            {stops.map((s) => <Coin key={s.id} stop={s} />)}
            {/* Live vehicle sprite — real Telluride cut-out */}
            {current && tab !== 'scout' && (
              <div className="absolute pointer-events-none" style={{ left: `${project(current.lat, current.lon).left + 3}%`, top: `${project(current.lat, current.lon).top + 2}%`, transform: 'translate(-50%,-50%)', zIndex: 25 }}>
                <div className="relative flex flex-col items-center">
                  <img src="/telluride.png" alt="Live vehicle" style={{ width: 72, filter: 'drop-shadow(0 0 7px rgba(34,197,94,0.9)) drop-shadow(0 2px 4px rgba(0,0,0,0.55))' }} />
                  <span className="relative flex w-3.5 h-3.5 -mt-1">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-80" />
                    <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-green-500 border-2 border-[#0b0b0d]" />
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* KEY legend */}
      {tab !== 'live' && (
        <div className="max-w-5xl mx-auto px-5 lg:px-10 pb-2" data-testid="r2m-key">
          <div className="border-2 border-white/10 bg-[#141416] p-4">
            <div className="text-center text-[10px] tracking-[0.4em] uppercase font-black text-white/50 mb-3">Key</div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-white/70">
              <span className="flex items-center gap-2"><span>🚀</span> The Launch Point</span>
              <span className="flex items-center gap-2"><span>🏀</span> Bucket Secured</span>
              <span className="flex items-center gap-2"><span>⭐</span> Destination · {cfg?.destination_label || 'Michigan'}</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-400" /> Live Broadcast (Kick / Twitch)</span>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 5: Monetization footer */}
      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-8">
        <div className="border-2 border-[#C9A961]/40 bg-gradient-to-r from-[#C9A961]/10 to-transparent p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="r2m-revenue-node">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-black text-[#C9A961] mb-1">The Revenue Node</div>
            <div className="text-lg sm:text-xl font-black uppercase tracking-tight">Support the run in real time</div>
            <div className="text-xs text-white/55 mt-1">Opens in a new tab — you never lose your place on the stream.</div>
          </div>
          <a href="https://streamlabs.com/prsvr/tip" target="_blank" rel="noopener noreferrer" data-testid="r2m-tip-btn"
            className="shrink-0 inline-flex items-center gap-2 bg-[#C9A961] text-black px-7 py-4 text-xs font-black tracking-[0.25em] uppercase hover:bg-[#d8bd7f] transition-colors">
            Fuel The Run // Support The Academy <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* SECTION 4: Slide-over panel */}
      {selected && (
        <div className="fixed inset-0 z-50" data-testid="r2m-panel">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="absolute right-0 top-0 h-full w-full sm:max-w-[400px] bg-[#141416] border-l border-white/10 shadow-2xl flex flex-col">
            <div className="flex items-start justify-between px-6 py-5 border-b border-white/10">
              <div>
                <div className="text-[9px] tracking-[0.3em] uppercase font-black text-[#C9A961]">{selected.node} · {selected.label}</div>
                <h2 className="text-2xl font-black uppercase tracking-tight mt-1">{selected.city}</h2>
                <div className="text-[11px] tracking-[0.15em] uppercase font-bold text-green-400 mt-1">{selected.status}</div>
              </div>
              <button onClick={() => setSelected(null)} data-testid="r2m-panel-close" className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {(selected.boys || []).length > 1 && (
              <div className="flex gap-2 px-6 pt-4">
                {selected.boys.map((b) => (
                  <button key={b} onClick={() => setActiveBoy(b)} data-testid={`r2m-boy-${b}`}
                    className={`flex items-center gap-2 px-3 py-2 text-[10px] font-black tracking-[0.15em] uppercase border transition-colors ${activeBoy === b ? 'bg-[#C9A961] text-black border-[#C9A961]' : 'border-white/20 text-white/70 hover:border-[#C9A961]'}`}>
                    <span className="w-5 h-5 rounded-full bg-[#C9A961] text-black text-[9px] font-black flex items-center justify-center overflow-hidden">
                      {boysMap[b]?.avatar ? <img src={boysMap[b].avatar} alt="" className="w-full h-full object-cover" /> : (boysMap[b]?.initials || b)}
                    </span>
                    {boysMap[b]?.name || b}
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="text-[10px] tracking-[0.25em] uppercase font-black text-white/50">{boysMap[activeBoy]?.name ? `${boysMap[activeBoy].name}'s Reel` : 'On-The-Road Footage'}</div>
              {selected.video ? (
                <button onClick={() => setVideoUrl(MEDIA(selected.video))} data-testid="r2m-play-video"
                  className="relative w-full aspect-video bg-[#0b0b0d] border border-[#C9A961]/50 overflow-hidden group/vid">
                  <video src={MEDIA(selected.video)} muted preload="metadata" className="absolute inset-0 w-full h-full object-cover opacity-70" />
                  <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/30">
                    <span className="w-14 h-14 rounded-full bg-[#C9A961] flex items-center justify-center group-hover/vid:scale-110 transition-transform"><Play className="w-6 h-6 text-black ml-0.5" fill="black" /></span>
                    <span className="text-[10px] tracking-[0.25em] uppercase font-black text-white">Tap To Play</span>
                  </span>
                </button>
              ) : (
                <div className="aspect-video bg-[#0b0b0d] border border-white/10 flex flex-col items-center justify-center gap-2">
                  <div className="w-12 h-12 rounded-full border-2 border-white/20 flex items-center justify-center"><Play className="w-5 h-5 text-white/30" /></div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">Clip Coming Soon</div>
                </div>
              )}
            </div>
            <div className="border-t border-white/10 p-6">
              <button onClick={() => navigate(selected.fit || '/chila')} data-testid="r2m-fit-cta"
                className="w-full flex items-center justify-center gap-2 bg-[#C9A961] text-black px-6 py-4 text-xs font-black tracking-[0.25em] uppercase hover:bg-[#d8bd7f] transition-colors">
                View On-Court Fit Specimen <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Video pop-up player */}
      {videoUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="r2m-video-modal" onClick={() => setVideoUrl(null)}>
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
          <button onClick={() => setVideoUrl(null)} data-testid="r2m-video-close"
            className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full bg-[#141416] border border-white/20 flex items-center justify-center text-white hover:bg-[#C9A961] hover:text-black transition-colors">
            <X className="w-5 h-5" />
          </button>
          <video
            src={videoUrl}
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
            className="relative z-[5] max-h-[85vh] w-auto max-w-[92vw] rounded-lg shadow-2xl bg-black"
          />
        </div>
      )}
    </div>
  );
};

export default RoadToMichigan;
